import axios from "axios";

// Fallback = endereço de produção do Central (NUNCA localhost: um build sem
// REACT_APP_HUB_API_URL caindo em localhost quebra o painel pra todo cliente —
// o navegador tenta a porta 4100 da máquina DELE). Em dev, exporte a var p/ localhost.
const API_URL = process.env.REACT_APP_HUB_API_URL || "https://hub.otimizapp.chat";

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

// Interceptor para adicionar API Key ou JWT
api.interceptors.request.use((config) => {
  const apiKey = localStorage.getItem("hub_api_key");
  const token = localStorage.getItem("hub_token");

  if (apiKey) {
    config.headers["X-Hub-Api-Key"] = apiKey;
  } else if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }

  return config;
});

// ===== API Methods =====

export const hubApi = {
  // Auth (email/senha -> JWT)
  login: (email, password) => api.post("/auth/login", { email, password }),

  // Esqueci minha senha / redefinição
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  resetPassword: (token, password) => api.post("/auth/reset-password", { token, password }),
  // Reset pelo super_admin: gera um link de redefinição para um usuário
  adminResetLink: (userId) => api.post(`/hub/admin/users/${userId}/reset-link`),

  // Licença e plano
  getLicense: () => api.get("/hub/license"),

  // Canais disponíveis
  getChannels: () => api.get("/hub/channels"),

  // Conexões
  getConnections: () => api.get("/hub/connections"),

  // Config Meta
  getMetaConfig: () => api.get("/hub/meta-config"),

  // Meta Embedded Signup
  metaEmbeddedSignup: (code, channel) =>
    api.post("/hub/meta-embedded-signup", { code, channel }),

  // Telegram (Bot Token)
  telegramConnect: (botToken, name) =>
    api.post("/hub/telegram-connect", { botToken, name }),

  // Telegram (Número Pessoal — MTProto)
  telegramPhoneConnect: (phoneNumber, name) =>
    api.post("/hub/telegram-connect", { phoneNumber, name }),
  telegramVerifyCode: (connectionId, code, phoneCodeHash) =>
    api.post("/hub/telegram-verify-code", { connectionId, code, phoneCodeHash }),
  telegramVerify2FA: (connectionId, password) =>
    api.post("/hub/telegram-verify-2fa", { connectionId, password }),

  // Email
  emailConnect: (params) => api.post("/hub/email-connect", params),

  emailDisconnect: (connectionId) =>
    api.post("/hub/email-disconnect", { connectionId }),

  // Desconectar conexão (mantém registro)
  disconnectConnection: (id) => api.post(`/hub/connections/${id}/disconnect`),

  // Reconectar conexão existente
  reconnectConnection: (id) => api.post(`/hub/connections/${id}/reconnect`),

  // Remover conexão permanentemente
  deleteConnection: (id) => api.delete(`/hub/connections/${id}`),

  // Passthrough de coexistência (Central-only)
  createPassthroughLink: (payload) => api.post("/hub/connect-session/passthrough", payload),
  connectionCredentials: (id) => api.get(`/hub/connections/${id}/credentials`),
  resyncConnectionWebhook: (id) => api.post(`/hub/connections/${id}/webhook/resync`),
  flipConnectionDestino: (id, payload) =>
    api.post(`/hub/connections/${id}/destino`, payload),

  // Enviar mensagem
  sendMessage: (connectionId, to, message) =>
    api.post("/hub/send", { connectionId, to, message }),

  // Portal redirect (marketplace)
  getPortalRedirect: (channel) =>
    api.get("/hub/portal-redirect", { params: { channel } }),

  // ===== VoIP =====
  voipConnect: (params) => api.post("/hub/voip/connect", params),
  voipGetConfig: (connectionId) => api.get(`/hub/voip/config/${connectionId}`),
  voipUpdateConfig: (connectionId, config) =>
    api.put(`/hub/voip/config/${connectionId}`, config),
  voipGetDashboard: (period = "30d") =>
    api.get("/hub/voip/dashboard", { params: { period } }),
  voipGetCallHistory: (params) =>
    api.get("/hub/voip/calls", { params }),
  voipGetCallDetail: (id) => api.get(`/hub/voip/calls/${id}`),
  voipGetRecording: (id) => `${API_URL}/hub/voip/calls/${id}/recording`,
  voipInitiateCall: (params) => api.post("/hub/voip/call/initiate", params),
  voipHangup: (callId) => api.post(`/hub/voip/call/${callId}/hangup`),
  voipTransfer: (callId, to) =>
    api.post(`/hub/voip/call/${callId}/transfer`, { to }),
  voipHold: (callId, hold = true) =>
    api.post(`/hub/voip/call/${callId}/hold`, { hold }),
  voipGetSIPCredentials: (connectionId, reveal = false) =>
    api.get(`/hub/voip/sip-credentials/${connectionId}`, {
      params: { reveal: reveal ? "true" : "false" },
    }),

  // ===== SIP Trunk =====
  sipConnect: (params) => api.post("/hub/sip/connect", params),
  sipDisconnect: (connectionId) =>
    api.post(`/hub/sip/disconnect/${connectionId}`),
  sipTest: (connectionId) => api.post(`/hub/sip/test/${connectionId}`),
  sipRegisterCall: (params) => api.post("/hub/sip/call/register", params),
  sipUpdateCallStatus: (callId, status, extra) =>
    api.post(`/hub/sip/call/${callId}/status`, { status, ...extra }),

  // ===== Connect Dashboard =====
  getDashboard: () => api.get("/hub/dashboard"),
  getDispatchMetrics: () => api.get("/hub/metrics/dispatch"),

  // ===== HUBs Locais =====
  getHubList: () => api.get("/hub/local/list"),
  deleteHub: (hubId) => api.delete(`/hub/local/${hubId}`),
  // Renovacao do connectionToken (Fase 15, Bloco A). Nao revoga nada: o token atual segue
  // valido ate o HUB Local confirmar que persistiu o novo.
  rotateHubToken: (hubId) => api.post(`/hub/local/${hubId}/rotate-token`),

  // ===== Registro de HUB Local (Fase 15, Bloco F) =====
  // No CENTRAL: emite token de registro de uso unico (hrt_) para um novo HUB Local.
  emitirTokenDeRegistroHub: (data) => api.post("/hub/admin/hubs-locais", data),
  listarTokensDeRegistro: () => api.get("/hub/admin/hubs-locais/tokens"),
  // No LOCAL: consome o token no Central e persiste o vinculo sozinho.
  registrarNoCentral: (data) => api.post("/hub/local/registrar", data),

  // ===== Admin Tenants (so HUB Central) =====
  adminListTenants: () => api.get("/hub/admin/tenants"),
  adminCreateTenant: (data) => api.post("/hub/admin/tenants", data),
  adminRegenerateKey: (id) => api.post(`/hub/admin/tenants/${id}/regenerate-key`),
  adminUpdateTenant: (id, data) => api.put(`/hub/admin/tenants/${id}`, data),
  adminUpdatePlan: (id, plan) => api.put(`/hub/admin/tenants/${id}/plan`, { plan }),
  adminListConnections: (filters = {}) => api.get("/hub/admin/connections", { params: filters }),
  getCentralStatus: () => api.get("/hub/central/status"),
  // Papel desta instancia (selo CENTRAL/LOCAL). Publica; devolve so { role }.
  getRole: () => api.get("/hub/role", { timeout: 10000 }),

  // ===== Settings =====
  getSettings: () => api.get("/hub/settings"),
  revealSecret: (key) => api.get(`/hub/settings/reveal/${key}`),
  updateSetting: (key, value) => api.put("/hub/settings", { key, value }),
  testService: (service) => api.post(`/hub/settings/test/${service}`),
  restartHub: () => api.post("/hub/settings/restart"),

  // ===== White Label =====
  getWhiteLabel: () => api.get("/hub/whitelabel"),
  updateWhiteLabel: (config) => api.put("/hub/whitelabel", config),

  // ===== Portal =====
  getPortalRedirect: (channel) =>
    api.get("/hub/portal-redirect", { params: { channel } }),
};

export default hubApi;
